import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { Button } from './Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './Dialog'

interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string
  /** Confirm button label. Defaults to "Delete". */
  confirmLabel?: string
  /** Confirm button label while the action runs. Defaults to "Deleting…". */
  pendingLabel?: string
  cancelLabel?: string
  /**
   * The action to run on confirm. While the returned promise is pending the
   * dialog shows a spinner and locks; it closes on resolve. If it throws, the
   * dialog stays open so the user can retry — surface your own toast in the
   * handler.
   */
  onConfirm: () => void | Promise<void>
}

/**
 * Shared T3 confirmation modal, extracted from the composer's delete dialog so
 * every "are you sure?" surface (calendar, cockpit, /posts) reads identically:
 * dark canvas, acid primary confirm, spinner while the action runs.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Delete',
  pendingLabel = 'Deleting…',
  cancelLabel = 'Cancel',
  onConfirm,
}: ConfirmDialogProps) {
  const [pending, setPending] = useState(false)

  const handleConfirm = async () => {
    if (pending) return
    setPending(true)
    try {
      await onConfirm()
      onOpenChange(false)
    } catch {
      // The caller surfaces its own error toast; keep the dialog open so the
      // user can retry rather than silently swallowing the failure.
    } finally {
      setPending(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (pending) return
        if (!next) onOpenChange(false)
      }}
    >
      <DialogContent size="sm" variant="dark" withCloseButton>
        <DialogHeader>
          <DialogTitle variant="dark">{title}</DialogTitle>
          <DialogDescription variant="dark">{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button
            variant="t3-primary"
            size="t3-md"
            onClick={() => void handleConfirm()}
            disabled={pending}
            className="w-full"
          >
            {pending ? (
              <>
                <Loader2 size={14} className="mr-2 animate-spin" />
                {pendingLabel}
              </>
            ) : (
              confirmLabel
            )}
          </Button>
          <Button
            variant="t3-tertiary"
            size="t3-md"
            onClick={() => onOpenChange(false)}
            disabled={pending}
            className="w-full"
          >
            {cancelLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
